import React, { useMemo, useState, useEffect } from 'react';
import { Tree } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import '../styles/TimelineBar.css';

const TimelineBar = ({ CreatedOn, onRangeSelect }) => {
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  const timelineData = useMemo(() => {
    if (!CreatedOn) return [];

    const start = dayjs(CreatedOn);
    const end = dayjs();
    const now = dayjs();

    const years = [];

    for (let y = start.year(); y <= end.year(); y++) {
      const isStartYear = y === start.year();
      const isEndYear = y === end.year();
      const months = [];

      const monthStart = isStartYear ? start.month() : 0;
      const monthEnd = isEndYear ? end.month() : 11;

      for (let m = monthStart; m <= monthEnd; m++) {
        const dayStart = isStartYear && m === start.month() ? start.date() : 1;
        const dayEnd =
          isEndYear && m === end.month()
            ? end.date()
            : dayjs(`${y}-${m + 1}`, "YYYY-M").daysInMonth();

        const weeks = [];
        for (let weekStart = dayStart; weekStart <= dayEnd; weekStart += 7) {
          const weekEnd = Math.min(weekStart + 6, dayEnd);

          const weekDays = [];
          for (let d = weekStart; d <= weekEnd; d++) {
            const dayKey = `${y}-${m + 1}-${d}`;
            const isToday = now.year() === y && now.month() === m && now.date() === d;
            const isSelected = selectedKey === dayKey;

            const classes = ['timeline-node'];
            if (isToday) classes.push('timeline-current');
            if (isSelected) classes.push('timeline-selected');

            weekDays.push({
              title: <span className={classes.join(' ')}>{d}</span>,
              key: dayKey,
              startDate: dayjs(`${y}-${m + 1}-${d}`).format("YYYY-MM-DD"),
              endDate: dayjs(`${y}-${m + 1}-${d}`).format("YYYY-MM-DD"),
              isLeaf: true,
              selectable: true,
            });
          }

          const weekNumber = Math.ceil(weekStart / 7);
          const weekKey = `${y}-${m + 1}-week-${weekNumber}`;
          const isCurrentWeek = y === now.year() && m === now.month() && weekNumber === Math.ceil(now.date() / 7);
          const isSelectedWeek = selectedKey === weekKey;

          const weekClasses = ['timeline-node'];
          if (isCurrentWeek) weekClasses.push('timeline-current');
          if (isSelectedWeek) weekClasses.push('timeline-selected');

          weeks.push({
            title: <span className={weekClasses.join(' ')}>Week {weekNumber}</span>,
            key: weekKey,
            startDate: dayjs(`${y}-${m + 1}-${weekStart}`).format("YYYY-MM-DD"),
            endDate: dayjs(`${y}-${m + 1}-${weekEnd}`).format("YYYY-MM-DD"),
            children: weekDays,
            selectable: true,
          });
        }

        const monthKey = `${y}-${m + 1}`;
        const isCurrentMonth = y === now.year() && m === now.month();
        const isSelectedMonth = selectedKey === monthKey;

        const monthClasses = ['timeline-node'];
        if (isCurrentMonth) monthClasses.push('timeline-current');
        if (isSelectedMonth) monthClasses.push('timeline-selected');

        months.push({
          title: <span className={monthClasses.join(' ')}>{dayjs().month(m).format("MMM")}</span>,
          key: monthKey,
          startDate: dayjs(`${y}-${m + 1}-01`).format("YYYY-MM-DD"),
          endDate: dayjs(`${y}-${m + 1}-01`).endOf("month").format("YYYY-MM-DD"),
          children: weeks,
          selectable: true,
        });
      }

      const yearKey = `${y}`;
      const isCurrentYear = y === now.year();
      const isSelectedYear = selectedKey === yearKey;

      const yearClasses = ['timeline-node'];
      if (isCurrentYear) yearClasses.push('timeline-current');
      if (isSelectedYear) yearClasses.push('timeline-selected');

      years.push({
        title: <span className={yearClasses.join(' ')}>{y}</span>,
        key: yearKey,
        startDate: dayjs(`${y}-01-01`).format("YYYY-MM-DD"),
        endDate: dayjs(`${y}-12-31`).format("YYYY-MM-DD"),
        children: months,
        selectable: true,
      });
    }

    return years;
  }, [CreatedOn, selectedKey]);

  // Auto-expand current Year, Month, Week
  useEffect(() => {
    const now = dayjs();
    const year = now.year();
    const month = now.month() + 1;
    const week = Math.ceil(now.date() / 7);

    setExpandedKeys([
      `${year}`,
      `${year}-${month}`,
      `${year}-${month}-week-${week}`,
    ]);
  }, []);

  return (
    <div className="timeline-container">
      <h4 className="timeline-header">Activity Timeline</h4>

      <Tree
        showLine
        switcherIcon={<DownOutlined />}
        treeData={timelineData}
        expandedKeys={expandedKeys}
        selectedKeys={selectedKey ? [selectedKey] : []}
        onExpand={setExpandedKeys}
        onSelect={(_, info) => {
          if (!info.node) return;

          setSelectedKey(info.node.key);

          const { startDate, endDate } = info.node;

          if (onRangeSelect) {
            onRangeSelect({ startDate, endDate });
          }
        }}
      />
    </div>
  );
};

export default TimelineBar;
